import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatINR, API, istMonth } from "@/lib/api";
import { AlertTriangle, CheckCircle2, Download, ChevronRight, ChevronDown, Wallet } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import MonthSelect from "@/components/MonthSelect";
import { StatusBadge } from "@/components/ui/AppPrimitives";

function readPayrollView() {
  try { return JSON.parse(sessionStorage.getItem("kaya:payroll-view") || "{}"); }
  catch { return {}; }
}

/**
 * Admin payroll summary — one row per employee for the selected month.
 * Net pay = Basic + Total incentive - Deductions.
 * Expandable rows expose the full incentive & deduction breakdown so admins can
 * verify the number before month-end payouts. CSV export mirrors the visible columns.
 */
export default function PayrollTab() {
  const savedView = readPayrollView();
  const [month, setMonth] = useState(savedView.month || istMonth());
  const [data, setData] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});   // employee_id → bool
  const [hideInactive, setHideInactive] = useState(savedView.hideInactive ?? true);
  const [lock, setLock] = useState({ locked: false });
  const [finalReport, setFinalReport] = useState(null);
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [unlockConfirm, setUnlockConfirm] = useState(false);
  useEffect(() => { sessionStorage.setItem("kaya:payroll-view", JSON.stringify({ month, hideInactive })); }, [month, hideInactive]);

  const load = () => {
    setLoading(true);
    api.get(`/admin/payroll?month=${month}`)
      .then((r) => setData({ rows: r.data.rows || [], totals: r.data.totals || null }))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
    api.get(`/admin/payroll/lock?month=${month}`)
      .then((r) => setLock(r.data || { locked: false }))
      .catch(() => setLock({ locked: false }));
    api.get(`/admin/payroll/finalization-report?month=${month}`)
      .then((r) => setFinalReport(r.data))
      .catch(() => setFinalReport(null));
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  const exportCsv = () => {
    const url = `${API}/admin/payroll/export?month=${month}`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `payroll-${month}.csv`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const finalize = async () => {
    try {
      const r = await api.post(`/admin/payroll/lock?month=${month}`);
      setLock(r.data);
      toast.success("Payroll finalized");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const unlock = async () => {
    try {
      await api.delete(`/admin/payroll/lock?month=${month}&confirmation=${encodeURIComponent(`UNLOCK ${month}`)}`);
      toast.success(`Payroll for ${month} unlocked`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const markIncentivesReviewed = async () => {
    try {
      await api.post(`/admin/payroll/review-incentives?month=${month}`);
      toast.success("Incentives marked reviewed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const rows = hideInactive ? data.rows.filter((r) => r.active) : data.rows;
  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="admin-payroll">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur flex items-center justify-between flex-wrap gap-3 py-2">
        <div>
          <div className="text-overline">Month-end</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Payroll summary</h2>
          <p className="text-sm text-[#5C635E] mt-1">Basic + Incentives - Deductions = <span className="font-medium">Net pay</span></p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {lock.locked && <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Finalized</span>}
          <MonthSelect testId="payroll-month" value={month} onChange={setMonth} />
          <label className="text-xs text-[#5C635E] flex items-center gap-2">
            <input data-testid="payroll-hide-inactive" type="checkbox" checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)} />
            Hide inactive
          </label>
          <button data-testid="payroll-export-csv" onClick={exportCsv}
            className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button data-testid="payroll-finalize" onClick={() => setFinalizeConfirm(true)} disabled={lock.locked || !finalReport?.ok}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm">
            {lock.locked ? "Finalized" : finalReport?.ok ? "Finalize month" : "Checklist incomplete"}
          </button>
          {lock.locked && <button data-testid="payroll-unlock" onClick={() => setUnlockConfirm(true)}
            className="border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg px-4 py-2 text-sm">
            Unlock month
          </button>}
        </div>
      </div>

      {lock.locked && <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 text-emerald-900">
        <StatusBadge status="finalized" /><div><div className="font-medium">This payroll month is locked</div><div className="text-sm mt-1">Values are preserved as the final salary record. Unlock the month before making payroll changes.</div></div>
      </div>}

      {/* KPI strip */}
      {totals.net_pay !== undefined && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="payroll-kpis">
          <Kpi label="Basic" value={formatINR(totals.basic_salary)} />
          <Kpi label="Daily wage" value={formatINR(totals.daily_wage)} />
          <Kpi label="Incentives" value={formatINR(totals.monthly_incentive)} />
          <Kpi label="Deductions" value={"− " + formatINR(totals.absent_deduction)} danger />
          <Kpi label="Salary till date" value={formatINR(totals.salary_till_date)} />
          <Kpi label="Net payout" value={formatINR(totals.net_pay)} accent />
        </div>
      )}

      {finalReport && (
        <div className="card-warm p-5" data-testid="payroll-finalization-report">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="text-overline">Before finalizing</div>
              <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Payroll finalization report</h3>
            </div>
            <button type="button" onClick={markIncentivesReviewed}
              className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-4 py-2 text-sm text-[#2D312E]">
              Mark incentives reviewed
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {(finalReport.checks || []).map((c) => (
              <div key={c.key} className={`border rounded-xl p-3 ${c.ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                <div className={`text-sm font-semibold flex items-center gap-2 ${c.ok ? "text-emerald-700" : "text-amber-700"}`}>
                  {c.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  {c.label}
                </div>
                <div className="text-xs text-[#5C635E] mt-1">{c.detail}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-warm overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm min-w-[1000px]" aria-label="Monthly payroll summary">
            <caption className="sr-only">Accrued salary till date and final full-month salary for each employee</caption>
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="p-3"></th>
                <th className="text-left p-3">Employee</th>
                <th className="text-right p-3">Basic</th>
                <th className="text-right p-3">Daily wage</th>
                <th className="text-right p-3">Incentive</th>
                <th className="text-right p-3 text-[#C05746]">Deduction</th>
                <th className="text-right p-3">Salary till date</th>
                <th className="text-right p-3 font-bold">Net pay</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-[#8A938D]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-[#8A938D]">No employees for this month.</td></tr>
              ) : rows.map((r) => (
                <React.Fragment key={r.employee_id}>
                  <tr data-testid={`payroll-row-${r.employee_id}`} className={`border-t border-[#EAE6DF] ${!r.active ? "opacity-60" : ""}`}>
                    <td className="p-3">
                      <button data-testid={`payroll-expand-${r.employee_id}`} onClick={() => toggle(r.employee_id)}
                        className="text-[#5C635E] hover:text-[#2D312E]" type="button">
                        {expanded[r.employee_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="p-3 font-medium text-[#2D312E]">
                      {r.employee_name}
                      {!r.active && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#8A938D] border border-[#EAE6DF]">inactive</span>}
                    </td>
                    <td className="p-3 text-right text-[#5C635E]">{formatINR(r.basic_salary)}</td>
                    <td className="p-3 text-right text-[#5C635E]">{formatINR(r.daily_wage)}</td>
                    <td className="p-3 text-right text-[#5C635E]">{formatINR(r.monthly_incentive)}</td>
                    <td className="p-3 text-right text-[#C05746]">− {formatINR(r.absent_deduction)}</td>
                    <td className="p-3 text-right font-medium text-[#2D312E]">{formatINR(r.salary_till_date)}</td>
                    <td className="p-3 text-right font-semibold text-[#4A5D4E]" data-testid={`payroll-net-${r.employee_id}`}>{formatINR(r.net_pay)}</td>
                  </tr>
                  {expanded[r.employee_id] && (
                    <tr data-testid={`payroll-detail-${r.employee_id}`} className="border-t border-[#EAE6DF] bg-[#FDFBF7]">
                      <td colSpan="8" className="p-4">
                        <DetailPanel row={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {rows.length > 0 && totals.net_pay !== undefined && (
              <tfoot>
                <tr className="border-t-2 border-[#4A5D4E] bg-[#F4F1EA] font-semibold">
                  <td></td>
                  <td className="p-3 text-[#2D312E]">Team totals ({rows.length})</td>
                  <td className="p-3 text-right text-[#2D312E]">{formatINR(rows.reduce((s, x) => s + x.basic_salary, 0))}</td>
                  <td className="p-3 text-right text-[#2D312E]">{formatINR(rows.reduce((s, x) => s + (x.daily_wage || 0), 0))}</td>
                  <td className="p-3 text-right text-[#2D312E]">{formatINR(rows.reduce((s, x) => s + x.monthly_incentive, 0))}</td>
                  <td className="p-3 text-right text-[#C05746]">− {formatINR(rows.reduce((s, x) => s + x.absent_deduction, 0))}</td>
                  <td className="p-3 text-right text-[#2D312E]">{formatINR(rows.reduce((s, x) => s + (x.salary_till_date || 0), 0))}</td>
                  <td className="p-3 text-right text-[#4A5D4E] text-base" data-testid="payroll-total-net">
                    {formatINR(rows.reduce((s, x) => s + x.net_pay, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <ConfirmDialog
        open={finalizeConfirm}
        title="Finalize payroll?"
        message={`Finalize and lock payroll for ${month}?${finalReport && !finalReport.ok ? "\n\nComplete every checklist item before finalizing." : ""}`}
        confirmLabel="Finalize"
        tone="warning"
        onConfirm={() => { setFinalizeConfirm(false); finalize(); }}
        onCancel={() => setFinalizeConfirm(false)}
      />
      <ConfirmDialog
        open={unlockConfirm}
        title="Unlock finalized payroll?"
        message={`Unlock payroll for ${month}? This allows attendance, incentive and donation changes for the month. The finalized snapshot is preserved for audit history.`}
        confirmLabel="Unlock month"
        tone="warning"
        onConfirm={() => { setUnlockConfirm(false); unlock(); }}
        onCancel={() => setUnlockConfirm(false)}
      />
    </div>
  );
}

function Kpi({ label, value, accent, danger }) {
  return (
    <div className={`card-warm p-4 ${accent ? "bg-[#4A5D4E] text-white" : ""}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "!text-white/70" : "text-[#8A938D]"}`}>{label}</div>
      <div className={`text-lg md:text-xl mt-1 ${accent ? "text-white" : danger ? "text-[#C05746]" : "text-[#2D312E]"}`} style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

function DetailPanel({ row }) {
  const att = row.attendance || {};
  const ib = row.incentive_breakdown || {};
  const db = row.deduction_breakdown || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
      <Card icon={<Wallet className="h-4 w-4" />} title="Attendance">
        <Row k="Present" v={att.present ?? 0} />
        <Row k="Late" v={att.late ?? 0} />
        <Row k="Half-day" v={att.half_day ?? 0} />
        <Row k="Absent" v={att.absent ?? 0} />
        <Row k="Hours worked" v={`${row.hours_worked ?? 0} h`} />
      </Card>

      <Card icon={<Wallet className="h-4 w-4" />} title={`Incentive breakdown${ib.manual_only ? " (manual)" : ""}`}>
        {ib.manual_only ? (
          <Row k="Manual" v={formatINR(ib.manual || 0)} />
        ) : (
          <>
            <Row k="Daily" v={formatINR(ib.daily || 0)} />
            <Row k="Hourly" v={formatINR(ib.hourly || 0)} />
            <Row k="Weekly" v={formatINR(ib.weekly || 0)} />
            <Row k="Monthly" v={formatINR(ib.monthly || 0)} />
            <Row k="Manual" v={formatINR(ib.manual || 0)} />
          </>
        )}
        <Row k="Total" v={formatINR(ib.total || row.monthly_incentive || 0)} strong />
      </Card>

      <Card icon={<Wallet className="h-4 w-4" />} title="Deductions">
        <Row k={`Absent (${(db.absent || {}).count ?? 0} × ${formatINR((db.absent || {}).per_day ?? 0)})`} v={"− " + formatINR((db.absent || {}).total ?? 0)} />
        <Row k={`Half-day (${(db.half_day || {}).count ?? 0} × ${formatINR((db.half_day || {}).per_day ?? 0)})`} v={"− " + formatINR((db.half_day || {}).total ?? 0)} />
        <Row k={`Late (${(db.late || {}).count ?? 0} × ${formatINR((db.late || {}).per_day ?? 0)})`} v={"− " + formatINR((db.late || {}).total ?? 0)} />
        <Row k="Total deduction" v={"− " + formatINR(row.absent_deduction || 0)} strong />
        <div className="text-[10px] text-[#8A938D] mt-1">Daily wage: {formatINR(db.daily_rate ?? 0)} · Calendar days: {db.calendar_days_in_month ?? "—"}</div>
      </Card>

      <Card icon={<Wallet className="h-4 w-4" />} title="Salary calculation">
        <Row k={`Accrued basic (${row.elapsed_calendar_days || 0} calendar days)`} v={formatINR(row.accrued_basic_salary || 0)} />
        <Row k="Less deductions" v={"− " + formatINR(row.absent_deduction || 0)} />
        <Row k="Salary till date" v={formatINR(row.salary_till_date || 0)} strong />
        <div className="text-[10px] text-[#8A938D] mt-1">Salary till date = daily wage × elapsed calendar days − applicable deductions.</div>
      </Card>
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="border border-[#EAE6DF] rounded-xl p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-[#8A938D] font-bold flex items-center gap-1 mb-2">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}
function Row({ k, v, strong }) {
  return (
    <div className={`flex justify-between text-xs py-0.5 ${strong ? "border-t border-[#EAE6DF] pt-1 mt-1 font-semibold text-[#2D312E]" : "text-[#5C635E]"}`}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

